import { configs } from '../configs';
import { authenticatedFetch } from '../utils/authenticatedFetch';

export interface QrLoginSession {
  sessionId: string;
  verificationCode: string;
  qrUrl: string;
  expiresIn: number; // in seconds
}

export interface QrLoginStatus {
  confirmed: boolean;
  confirmedUserId?: string;
  expired: boolean;
  used?: boolean;
}

export interface QrLoginTokenResponse {
  success: boolean;
  user: any;
  token: string;
}

class QrLoginService {
  /**
   * Generate a new QR login session
   */
  async generateSession(): Promise<QrLoginSession> {
    const url = `${configs.apiRoot}/authentication/qr-login/generate`;
    console.log('[QRLoginService] Generating session, URL:', url);
    
    const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
    });

    console.log('[QRLoginService] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const error = await response.json();
      console.error('[QRLoginService] Error response:', error);
      throw new Error(error.message || 'Failed to generate QR login session');
    }

    const data = await response.json();
    return {
      sessionId: data.sessionId,
      verificationCode: data.verificationCode,
      qrUrl: data.qrUrl,
      expiresIn: data.expiresIn,
    };
  }

  /**
   * Check session status (for polling)
   */
  async checkStatus(sessionId: string): Promise<QrLoginStatus> {
    const response = await authenticatedFetch(
      `${configs.apiRoot}/authentication/qr-login/${sessionId}/status`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      if (response.status === 404) {
        return {
          confirmed: false,
          expired: true,
        };
      }
      throw new Error(error.message || 'Failed to check session status');
    }

    const data = await response.json();
    return {
      confirmed: data.confirmed,
      confirmedUserId: data.confirmedUserId,
      expired: false,
      used: data.used,
    };
  }

  /**
   * Confirm login from mobile
   */
  async confirmLogin(
    sessionId: string,
    verificationCode: string
  ): Promise<{ success: boolean; message?: string }> {
    const response = await authenticatedFetch(
      `${configs.apiRoot}/authentication/qr-login/${sessionId}/confirm`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          verificationCode,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      return {
        success: false,
        message: error.message || 'Failed to confirm login',
      };
    }

    const data = await response.json();
    return {
      success: data.success,
      message: data.message,
    };
  }

  /**
   * Exchange session for token (after confirmation)
   */
  async exchangeSession(sessionId: string): Promise<QrLoginTokenResponse> {
    const response = await authenticatedFetch(
      `${configs.apiRoot}/authentication/qr-login/${sessionId}/exchange`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to exchange session for token');
    }

    const data = await response.json();
    return {
      success: data.success,
      user: data.user,
      token: data.token,
    };
  }
}

export const qrLoginService = new QrLoginService();
